import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Tooltip } from '@radix-ui/themes';

import Text from '../../../../../shared/Text';

import { useUpdatePostMutation } from '../hooks/usePostMutation';
import { useChangePostDate } from '../hooks/useChangePostDate.jsx';
import { useCreatePost } from '../hooks/useCreatePost.jsx';
import { useCampaignContext } from '../../../contexts/CampaignContext.jsx';

import { PostItem } from './PostItem.jsx';

export const PostList = ({ posts, campaignUuid }) => {
  const tiles = useSelector(state => state.campaignDnd);
  const { t } = useTranslation('posts');
  const { campaign } = useCampaignContext();
  const isNewHireWithPost = campaign.newHire && posts.length >= 1;
  const isDisabled =
    campaign.isArchived || !!campaign.deletedAt || isNewHireWithPost;

  const { updatePostMutation } = useUpdatePostMutation();
  const { openChangeDateModal, ChangePostDateModal } = useChangePostDate();
  const { openCreatePostModal, CreatePostModal } = useCreatePost();

  const tilesCount = posts.reduce((result, post) => {
    return result + tiles[`post-${post.uuid}`]?.length;
  }, 0);

  const onClick = () => {
    if (isDisabled) return null;

    return openCreatePostModal(campaignUuid);
  };

  return (
    <PostListContainer>
      <ChangePostDateModal />
      <CreatePostModal />
      {posts.map(post => (
        <PostItem
          key={post.uuid}
          post={post}
          tiles={tiles}
          campaignUuid={campaignUuid}
          updatePostMutation={updatePostMutation}
          openChangeDateModal={openChangeDateModal}
          t={t}
        />
      ))}
      <Footer>
        <Tooltip
          content={
            isNewHireWithPost ? t('postSummary.newHireSinglePostLimit') : null
          }
        >
          <AddPostButton onClick={onClick} isDisabled={isDisabled}>
            <AddPostIcon isDisabled={isDisabled} />
            <Text
              color={isDisabled ? 'black.60' : 'primary.100'}
              variant="button"
              display="block"
            >
              {t('postSummary.addPost')}
            </Text>
          </AddPostButton>
        </Tooltip>
        <TileCount>
          <Text color="black.100" variant="subtitle2" display="block">
            {t('postSummary.totalTilesCount', { tilesCount })}
          </Text>
        </TileCount>
      </Footer>
    </PostListContainer>
  );
};

const Footer = styled.div`
  display: flex;
  margin-top: 21px;
  justify-content: space-between;
`;

const TileCount = styled.div`
  border-radius: 4px;
  background: ${themeGet('colors.black.5')};
  padding: 4px 8px;
`;

const AddPostButton = styled.div`
  display: flex;
  align-items: center;
  &:hover {
    cursor: ${props => (props.isDisabled ? 'not-allowed' : 'pointer')};
  }
  ${Text}:hover {
    text-decoration: underline;
  }
`;

const AddPostIcon = styled.i.attrs({
  className: 'fa-regular fa-circle-plus',
})`
  color: ${props =>
    props.isDisabled
      ? themeGet('colors.black.60')
      : themeGet('colors.primary.100')};
  margin-right: 6px;
`;

const PostListContainer = styled.div`
  text-align: left;
`;
